<template>
  <UApp :locale="ro">
    <div class="error-page min-h-screen flex flex-col">
      <div class="error-page__glow" aria-hidden="true" />

      <header class="relative z-10 px-4 pt-8 pb-4">
        <div class="mx-auto max-w-3xl flex items-center justify-between">
          <button
            type="button"
            class="text-sm font-semibold text-brand-700 hover:text-brand-800 transition-colors"
            @click="goHome"
          >
            ← Înapoi acasă
          </button>
          <span class="text-xs font-medium uppercase tracking-wider text-toned">
            {{ statusLabel }}
          </span>
        </div>
      </header>

      <main class="relative z-10 flex-1 flex items-center justify-center px-4 py-10">
        <div class="error-page__card w-full max-w-lg text-center">
          <p class="error-page__code" aria-hidden="true">
            {{ displayCode }}
          </p>

          <div
            class="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl"
            :class="isNotFound ? 'bg-brand-100 text-brand-700' : 'bg-red-100 text-red-700'"
          >
            <UIcon
              :name="isNotFound ? 'i-heroicons-magnifying-glass' : 'i-heroicons-exclamation-triangle'"
              class="h-8 w-8"
            />
          </div>

          <h1 class="text-2xl md:text-3xl font-bold text-highlighted tracking-tight mb-3">
            {{ title }}
          </h1>
          <p class="text-toned text-base leading-relaxed mb-8 max-w-md mx-auto">
            {{ description }}
          </p>

          <div class="flex flex-col sm:flex-row gap-3 justify-center">
            <UButton
              :label="isNotFound ? 'Mergi la pagina principală' : 'Încearcă din nou'"
              color="primary"
              size="lg"
              @click="isNotFound ? goHome() : handleRetry()"
            />
            <UButton
              v-if="isNotFound"
              label="Vezi produsele"
              variant="solid"
              size="lg"
              @click="goProducts"
            />
            <UButton
              v-else
              label="Contact"
              variant="solid"
              size="lg"
              @click="goContact"
            />
          </div>
        </div>
      </main>

      <footer class="relative z-10 px-4 pb-8 text-center text-xs text-muted">
        Dacă problema persistă, scrie-ne pe pagina de contact.
      </footer>
    </div>
  </UApp>
</template>

<script setup lang="ts">
import type { NuxtError } from '#app'
import { ro } from '@nuxt/ui/locale'

const props = defineProps<{
  error: NuxtError
}>()

const shopPath = useShopPath()

const statusCode = computed(() => {
  const raw = props.error?.statusCode
  const parsed = typeof raw === 'string' ? Number.parseInt(raw, 10) : Number(raw)
  return Number.isFinite(parsed) ? parsed : 500
})

const isNotFound = computed(() => statusCode.value === 404)
const displayCode = computed(() => String(statusCode.value || 500))
const statusLabel = computed(() => (isNotFound.value ? 'Pagină lipsă' : 'Eroare server'))

const title = computed(() => {
  if (isNotFound.value) return 'Nu am găsit pagina asta'
  return 'Ceva nu a mers cum trebuie'
})

const description = computed(() => {
  if (isNotFound.value) {
    return 'Linkul e greșit, pagina a fost mutată sau magazinul nu există. Poți continua din pagina principală sau din catalog.'
  }

  const message = String(props.error?.statusMessage || props.error?.message || '').trim()
  // Hide internal/technical messages from visitors
  if (
    message
    && !/hasOwnProperty|internal server error|Cannot |ECONN|fetch failed/i.test(message)
  ) {
    return message
  }

  return 'Ne pare rău — am întâmpinat o eroare temporară. Reîncearcă în câteva momente.'
})

function goHome() {
  clearError({ redirect: shopPath('/') })
}

function goProducts() {
  clearError({ redirect: shopPath('/produse') })
}

function goContact() {
  clearError({ redirect: shopPath('/contact') })
}

function handleRetry() {
  clearError({ redirect: shopPath('/') })
}

useSeo({
  title: isNotFound.value ? 'Pagină negăsită' : 'Eroare',
  noindex: true,
})
</script>

<style scoped>
.error-page {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(1200px 500px at 50% -10%, color-mix(in srgb, var(--color-brand-200, #bfdbfe) 55%, transparent), transparent 70%),
    linear-gradient(180deg, var(--ui-bg, #ffffff) 0%, color-mix(in srgb, var(--color-brand-50, #eff6ff) 65%, var(--ui-bg, #ffffff)) 100%);
}

.error-page__glow {
  pointer-events: none;
  position: absolute;
  inset: auto -20% -30% -20%;
  height: 55%;
  background: radial-gradient(ellipse at center, color-mix(in srgb, var(--color-brand-300, #93c5fd) 25%, transparent), transparent 70%);
}

.error-page__card {
  position: relative;
  border-radius: 1.5rem;
  border: 1px solid color-mix(in srgb, var(--ui-border, #e2e8f0) 80%, transparent);
  background: color-mix(in srgb, var(--ui-bg, #ffffff) 88%, transparent);
  backdrop-filter: blur(10px);
  padding: 2.5rem 1.5rem;
  box-shadow:
    0 1px 2px rgb(15 23 42 / 4%),
    0 16px 40px rgb(15 23 42 / 6%);
}

.error-page__code {
  position: absolute;
  inset-inline: 0;
  top: 0.75rem;
  font-size: clamp(4.5rem, 18vw, 7rem);
  font-weight: 800;
  letter-spacing: -0.06em;
  line-height: 1;
  color: color-mix(in srgb, var(--color-brand-600, #2563eb) 10%, transparent);
  user-select: none;
  pointer-events: none;
}

@media (min-width: 640px) {
  .error-page__card {
    padding: 3rem 2.5rem;
  }
}
</style>
